import { Flex, Typography, Spin, Pagination } from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SearchResultsTable from "@/components/tables/SearchResultsTable";
import ShowDeletedTasksSwitch from "@/components/inputs/ShowDeletedTasksSwitch";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getPluralizedWord } from "@/utils";

import { useSearchData } from "./useSearchData";
import SearchFilters from "./SearchFilters";
import routes from "@/routes";
import SearchFileModal from "@/components/modals/SearchFileModal";
import { FILTER_NAMES } from "./constants";

// Страница результатов глобального поиска.
const Search = ({ isAdmins = false }) => {
  const {
    optionsPending,
    units,
    unitsTree,
    data,
    total,
    pending,
    tableParams,
    searchInput,
    isFiltersModalShow,
    tempFilters,
    selectedSections,
    tempSelectedSections,
    isAllSectionsSelected,
    isAllTempSectionsSelected,
    isFileModalOpen,
    selectedFile,
    applySearch,
    handleSearchChange,
    handleFilterChange,
    handleSortChange,
    handlePaginationChange,
    handleToggleAllSections,
    openFiltersModal,
    handleTempFilterChange,
    handleApplyModalFilters,
    handleResetModalFilters,
    handleCancelModalFilters,
    handleToggleAllTempSections,
    handleOpenFileModal,
    handleCloseFileModal,
    handleFileDownload,
    handleOpenFolder,
  } = useSearchData({ isAdmins });

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.select_view,
    tableParams.pagination.pageSize,
  ]);

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25} className="h-100">
            <Breadcrumbs
              items={
                isAdmins
                  ? [
                      {
                        title: "Административная панель",
                      },
                      {
                        title: "Поиск",
                      },
                    ]
                  : [
                      {
                        title: "Главная",
                        href: routes.dashboards,
                      },
                      {
                        title: "Поиск",
                      },
                    ]
              }
            />

            <SearchFilters
              isAdmins={isAdmins}
              pending={pending}
              searchInput={searchInput}
              units={units}
              unitsTree={unitsTree}
              tableParams={tableParams}
              selectedSections={selectedSections}
              isAllSectionsSelected={isAllSectionsSelected}
              tempFilters={tempFilters}
              tempSelectedSections={tempSelectedSections}
              isAllTempSectionsSelected={isAllTempSectionsSelected}
              isFiltersModalShow={isFiltersModalShow}
              applySearch={applySearch}
              handleSearchChange={handleSearchChange}
              handleFilterChange={handleFilterChange}
              handleSortChange={handleSortChange}
              handleToggleAllSections={handleToggleAllSections}
              openFiltersModal={openFiltersModal}
              handleCancelModalFilters={handleCancelModalFilters}
              handleTempFilterChange={handleTempFilterChange}
              handleApplyModalFilters={handleApplyModalFilters}
              handleResetModalFilters={handleResetModalFilters}
              handleToggleAllTempSections={handleToggleAllTempSections}
            />

            {/* Показываем только после выполненного поиска. */}
            {data !== null && (
              <>
                <Typography.Title level={2} className="no-margin">
                  {pending
                    ? "Ищем..."
                    : `Найдено ${total} ${getPluralizedWord(total, ["объект", "объекта", "объектов"])}`}
                </Typography.Title>

                <ShowDeletedTasksSwitch
                  checked={tableParams.filters.include_deleted}
                  disabled={pending}
                  onChange={(checked) =>
                    handleFilterChange(FILTER_NAMES.INCLUDE_DELETED, checked)
                  }
                />

                {/* Контент */}
                <Flex vertical gap={20} style={{ flex: 1, minHeight: 0 }}>
                  <div
                    ref={tableContainerRef}
                    style={{ flex: 1, minHeight: 0 }}
                  >
                    <SearchResultsTable
                      data={data}
                      loading={pending}
                      pagination={false}
                      isAdmins={isAdmins}
                      scroll={{ x: "100%", y: tableHeight - headerHeight }}
                      setFileModal={handleOpenFileModal}
                    />
                  </div>

                  <div style={{ minHeight: 32 }}>
                    <Pagination
                      {...tableParams.pagination}
                      total={total}
                      onChange={handlePaginationChange}
                    />
                  </div>
                </Flex>
              </>
            )}
          </Flex>
          {isFileModalOpen && (
            <SearchFileModal
              file={selectedFile}
              visible={isFileModalOpen}
              isAdmin={isAdmins}
              onCancel={handleCloseFileModal}
              onDownload={handleFileDownload}
              onOpenFolder={handleOpenFolder}
            />
          )}
        </>
      )}
    </>
  );
};

export default Search;
